import { useCompany } from '@hooks';
import { TicketService } from '@services';
import { Sparkles } from 'lucide-react';
import React, { FC, ReactNode, useState } from 'react';
import { Check } from 'react-feather';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';

interface GuardProps {
  children: ReactNode;
  modules: string[];
}

export const ModuleGuard: FC<GuardProps> = ({ children, modules }) => {
  const registredModules = useSelector(
    (state: RootState) => state.auth.modules
  );
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent'>('idle');
  const { companyId } = useCompany();
  const ticketService = new TicketService();

  const hasAllModules = modules.every((required) =>
    registredModules.some(
      (m) => m.mod_name.toLowerCase() === required.toLowerCase()
    )
  );

  const handleRequestInfo = async () => {
    try {
      setStatus('loading');

      const currentUrl = window.location.href;

      await ticketService.createTicketZendesk(companyId, {
        department: 'Telecom',
        subject: 'Aanvraag nieuwe module',
        description: `
Vraag om contact op te nemen over modules: ${modules.join(', ')}

Pagina: ${currentUrl}
        `.trim(),
      });

      setStatus('sent');
    } catch (error) {
      console.error(error);
      setStatus('idle');
    }
  };

  if (!hasAllModules) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="max-w-md w-full 
          bg-white dark:bg-gray-900 
          border border-gray-200 dark:border-gray-700 
          rounded-xl shadow-sm p-6 text-center"
        >
          <div className="flex justify-center mb-3">
            <Sparkles className="text-gray-400 dark:text-gray-500" size={22} />
          </div>

          <h2
            className="text-lg font-medium 
            text-gray-800 dark:text-gray-100 
            mb-1"
          >
            Deze feature zit niet in je pakket 🙂
          </h2>

          <p
            className="text-sm 
            text-gray-600 dark:text-gray-400 
            mb-4"
          >
            Wil je dit gebruiken? Laat het ons weten, we helpen je graag.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {modules.map((module) => (
              <span
                key={module}
                className="
                  bg-gray-100 text-gray-600 
                  dark:bg-gray-800 dark:text-gray-300 
                  px-2 py-1 rounded-full text-xs
                "
              >
                {module}
              </span>
            ))}
          </div>

          <button
            onClick={handleRequestInfo}
            disabled={status !== 'idle'}
            className={`mt-12 px-4 py-2 text-sm rounded-lg transition flex items-center justify-center gap-2 mx-auto
              ${
                status === 'sent'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 cursor-default'
                  : 'bg-gray-800 text-white hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-60'
              }
            `}
          >
            {status === 'idle' && 'Vraag meer info'}
            {status === 'loading' && 'Bezig...'}
            {status === 'sent' && (
              <>
                <Check size={16} />
                Verzonden
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ModuleGuard;
